import React from 'react';
import { cn } from '../../utils/cn';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'hover' | 'elevated' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  role?: string;
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export const Card: React.FC<CardProps> = ({ 
  children, 
  className, 
  variant = 'default',
  padding = 'md',
  onClick,
  role,
  tabIndex,
  onKeyDown
}) => {
  const baseClasses = 'card-base';
  
  const variantClasses = {
    default: '',
    hover: 'card-hover cursor-pointer transition-all duration-200 hover:shadow-card-hover hover:-translate-y-1',
    elevated: 'shadow-card-hover',
    outlined: 'border border-border-subtle shadow-none'
  };

  const paddingClasses = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
    onKeyDown?.(event);
  };

  return (
    <div 
      className={cn(
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        onClick && 'focus:outline-none focus:ring-2 focus:ring-primary-navy focus:ring-offset-2',
        className
      )}
      onClick={onClick}
      role={onClick ? (role || 'button') : role}
      tabIndex={onClick ? (tabIndex ?? 0) : tabIndex}
      onKeyDown={onClick ? handleKeyDown : onKeyDown}
    >
      {children}
    </div>
  );
};